/**
 * Monitoring & Analytics Utilities
 * Provides error tracking and analytics functionality
 */

// Initialize Google Analytics
export function initAnalytics() {
  const gaId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;

  if (!gaId) {
    console.log('[Analytics] No GA ID configured, analytics disabled');
    return;
  }

  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  gtag('js', new Date());
  gtag('config', gaId);

  // Expose gtag globally
  (window as any).gtag = gtag;

  console.log('[Analytics] Google Analytics initialized');
}

// Track page view
export function trackPageView(path: string, title?: string) {
  if ((window as any).gtag) {
    (window as any).gtag('config', import.meta.env.VITE_GOOGLE_ANALYTICS_ID, {
      page_path: path,
      page_title: title,
    });
  }
}

// Track event
export function trackEvent(
  action: string,
  category: string,
  label?: string,
  value?: number
) {
  if ((window as any).gtag) {
    (window as any).gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
}

// Initialize Sentry error tracking
export function initErrorTracking() {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  if (!sentryDsn) {
    console.log('[Sentry] No DSN configured, error tracking disabled');
    return;
  }

  // For now, basic error logging - Sentry SDK would be added here
  // when @sentry/react is installed
  console.log('[Sentry] Error tracking would be initialized with DSN');

  // Global error handler
  window.addEventListener('error', (event) => {
    console.error('[Error Tracking]', event.error);
    // Would send to Sentry when SDK is installed
  });

  window.addEventListener('unhandledrejection', (event) => {
    console.error('[Error Tracking] Unhandled Promise Rejection:', event.reason);
    // Would send to Sentry when SDK is installed
  });
}

// Capture error
export function captureError(error: Error, context?: Record<string, any>) {
  console.error('[Error Capture]', error, context);
  // Would send to Sentry when SDK is installed
}

// Capture message
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  console.log(`[${level.toUpperCase()}]`, message);
  // Would send to Sentry when SDK is installed
}

// Initialize all monitoring
export function initMonitoring() {
  initAnalytics();
  initErrorTracking();
}

// TypeScript declarations for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
